import React, { useState, useEffect } from 'react';
import {
  Heart,
  Dna,
  BarChart3,
  Swords,
  TrendingUp,
  Target,
  Infinity,
  CircleDot,
  Building2,
  Trophy,
} from 'lucide-react';
import './SplashPage.css';

interface SplashPageProps {
  isSignedIn?: boolean;
  onSignUp?: () => void;
}

const SplashPage: React.FC<SplashPageProps> = ({ isSignedIn, onSignUp }) => {
  const [year, setYear] = useState(1900);
  const [day, setDay] = useState(0);

  // Animate the "current season" counter
  useEffect(() => {
    const interval = setInterval(() => {
      setDay(prev => (prev + 1) % 162);
      if (Math.random() < 0.1) setYear(prev => prev + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // NFT Drop countdown timer
  useEffect(() => {
    const DROP_DATE = new Date('2026-05-17T12:00:00Z');
    const updateCountdown = () => {
      const now = new Date();
      const diff = DROP_DATE.getTime() - now.getTime();
      if (diff <= 0) {
        document.getElementById('nft-hours')!.textContent = '00';
        document.getElementById('nft-mins')!.textContent = '00';
        document.getElementById('nft-secs')!.textContent = '00';
        return;
      }
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      document.getElementById('nft-hours')!.textContent = String(hrs).padStart(2, '0');
      document.getElementById('nft-mins')!.textContent = String(mins).padStart(2, '0');
      document.getElementById('nft-secs')!.textContent = String(secs).padStart(2, '0');
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const navigate = (to: string) => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: to }));
  };

  return (
    <div className="splash-page">
      {/* NFT Drop Countdown — TOP OF PAGE */}
      <section className="splash-nft-section">
        <div className="nft-glow" />
        <h2>🧟 Genesis Monster Collection</h2>
        <p className="nft-subtitle">These 600 NFTs are Generation 0 for all SimForge baseball players. Forge any male + female to create a new player — 90% of first-gen player sales go directly to NFT holders.</p>
        
        <div className="nft-countdown">
          <span className="countdown-label">Next Drop In</span>
          <div className="countdown-timer" id="nft-countdown-timer">
            <div className="countdown-unit"><span className="countdown-num" id="nft-hours">--</span><span>hrs</span></div>
            <div className="countdown-unit"><span className="countdown-num" id="nft-mins">--</span><span>mins</span></div>
            <div className="countdown-unit"><span className="countdown-num" id="nft-secs">--</span><span>secs</span></div>
          </div>
        </div>

        <div className="nft-monsters-grid">
          <div className="nft-monster-card">
            <img src="/nft-previews/vampire.png" alt="Vampire" className="monster-img" />
            <h3>Vampire</h3>
            <span className="monster-count">100 total · 20 per wave</span>
          </div>
          <div className="nft-monster-card">
            <img src="/nft-previews/frankenstein.png" alt="Frankenstein" className="monster-img" />
            <h3>Frankenstein</h3>
            <span className="monster-count">100 total · 20 per wave</span>
          </div>
          <div className="nft-monster-card">
            <img src="/nft-previews/werewolf.png" alt="Werewolf" className="monster-img" />
            <h3>Werewolf</h3>
            <span className="monster-count">100 total · 20 per wave</span>
          </div>
          <div className="nft-monster-card">
            <img src="/nft-previews/creature.png" alt="Creature" className="monster-img" />
            <h3>Creature</h3>
            <span className="monster-count">100 total · 20 per wave</span>
          </div>
          <div className="nft-monster-card">
            <img src="/nft-previews/ghoul.png" alt="Ghoul" className="monster-img" />
            <h3>Ghoul</h3>
            <span className="monster-count">100 total · 20 per wave</span>
          </div>
          <div className="nft-monster-card">
            <img src="/nft-previews/mummy.png" alt="Mummy" className="monster-img" />
            <h3>The Mummy</h3>
            <p>Ancient bandages, golden accents, Egyptian mystique. Timeless terror.</p>
            <span className="monster-count">100 total · 20 per wave</span>
          </div>
        </div>

        <div className="nft-price-box">
          <div className="price-tiers">
            <div className="price-tier presale">
              <span className="tier-label">🎟️ Presale</span>
              <span className="tier-price">0.3 SOL</span>
              <span className="tier-sub">Now until May 17 · 12PM UTC</span>
            </div>
            <div className="price-tier public">
              <span className="tier-label">🌐 Public</span>
              <span className="tier-price">0.3 SOL</span>
              <span className="tier-sub">After May 17 · 12PM UTC</span>
            </div>
          </div>
        </div>

        <div className="nft-cta">
          <a
            href="https://magiceden.io/mint-details/8HGNnQSK1oNyLY4jLGBoSd2DtGt8yTAKpPEexJvMoZGo"
            target="_blank"
            rel="noopener noreferrer"
            className="splash-cta primary big pulse"
          >
            🎨 Mint Now — 0.3 SOL
          </a>
          <p className="nft-cta-sub">120 unique monsters · 6 types</p>
        </div>
      </section>

      {/* How It Works — Forging Teaser */}
      <section className="splash-forge-teaser">
        <div className="forge-teaser-content">
          <h3>🧬 How Forging Works</h3>
          <div className="forge-steps">
            <div className="forge-step">
              <span className="forge-step-icon">🧛+🧟</span>
              <span className="forge-step-text">Own 2 NFTs</span>
            </div>
            <span className="forge-arrow">→</span>
            <div className="forge-step">
              <span className="forge-step-icon">🔮</span>
              <span className="forge-step-text">Forge them together</span>
            </div>
            <span className="forge-arrow">→</span>
            <div className="forge-step">
              <span className="forge-step-icon">❓</span>
              <span className="forge-step-text">Reveal your new player</span>
            </div>
          </div>
          <p className="forge-teaser-sub">Combine any male + female NFT to create a unique player portrait. Forge to find out what the offspring looks like!</p>
        </div>
      </section>

      {/* Hero */}
      <section className="splash-hero">
        <div className="splash-hero-bg" />
        <div className="splash-hero-content">
          <div className="splash-logo">
            <img src="/logo.png" alt="SimForge Baseball" className="splash-logo-img" />
          </div>
          <p className="splash-tagline">
            Draft. Manage. Simulate. <span className="tagline-accent">Repeat Forever.</span>
          </p>
          <p className="splash-sub">
            Build your roster, manage your team, and watch every game unfold in a living, breathing baseball world powered by a next-gen simulation engine.
          </p>
          <div className="splash-hero-actions">
            <button className="splash-cta primary big" onClick={() => navigate('app')}>
              Enter SimForge →
            </button>
          </div>
          
        </div>
      </section>


      {/* Features */}
      <section className="splash-features">
        <h2 className="section-title">What You Get</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon"><Dna size={36} /></div>
            <h3>Breed & Forge Players</h3>
            <p>Retire your best players and breed them to create the next generation. Pass down traits, mix attributes, chase that perfect prospect.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><BarChart3 size={36} /></div>
            <h3>Real Simulation Engine</h3>
            <p>Pitcher-batter matchups, exit velocity, launch angle, BABIP — every at-bat is simulated with stats that mirror real baseball.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Swords size={36} /></div>
            <h3>Compete in Leagues</h3>
            <p>Join tiered leagues with prize pools. Promote, relegate, and fight your way up from Bronze to Diamond.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><TrendingUp size={36} /></div>
            <h3>Player Growth</h3>
            <p>Watch your players develop over time. Young prospects improve, veterans decline — manage their careers like a real GM.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Target size={36} /></div>
            <h3>Team Strategy</h3>
            <p>Set steal attempts, sacrifice bunts, hitting approach, and base running aggression. Your strategy drives the sim.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Infinity size={36} /></div>
            <h3>Perpetual Seasons</h3>
            <p>No game-over. New seasons start automatically. Build dynasties across decades, not just months.</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="splash-how-it-works">
        <h2 className="section-title">How It Works</h2>
        <div className="steps-row">
          <div className="step-card">
            <span className="step-number">1</span>
            <h3>Create or Claim</h3>
            <p>Generate your first player or claim ownership of an existing team.</p>
          </div>
          <div className="step-connector" />
          <div className="step-card">
            <span className="step-number">2</span>
            <h3>Set Your Strategy</h3>
            <p>Configure lineups, set strategy sliders, decide your team's identity.</p>
          </div>
          <div className="step-connector" />
          <div className="step-card">
            <span className="step-number">3</span>
            <h3>Watch It Sim</h3>
            <p>The engine runs around the clock. Every game simulated. Every stat tracked.</p>
          </div>
          <div className="step-connector" />
          <div className="step-card">
            <span className="step-number">4</span>
            <h3>Breed & Repeat</h3>
            <p>Retire stars, forge new legends, grow your roster. The next dynasty starts now.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="splash-cta-section">
        <h2>Ready to Play?</h2>
        <p>Every great dynasty started with a single player.</p>
        <div className="splash-cta-buttons">
          <button className="splash-cta primary big" onClick={() => navigate('create-player')}>
            <CircleDot size={18} /> Create Your First Player
          </button>
          <button className="splash-cta secondary big" onClick={() => navigate('buy-team')}>
            <Building2 size={18} /> Buy a Team
          </button>
        </div>
      </section>


      {/* Professional Footer */}
      <footer className="splash-footer">
        <div className="footer-top">
          <div className="footer-brand">
            <span className="footer-logo"><CircleDot size={20} /> SimForge Baseball</span>
            <p className="footer-tagline">A living baseball simulation</p>
          </div>
        </div>
        <div className="footer-nav">
          <button className="footer-nav-link" onClick={() => navigate('create-player')}>Play</button>
          <button className="footer-nav-link" onClick={() => navigate('leagues')}>Leagues</button>
          <button className="footer-nav-link" onClick={() => navigate('whitepaper')}>White Paper</button>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 SimForge Baseball. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default SplashPage;
